/**
 * Session Link Login Component
 *
 * The way in an administrator's session link opens: ?mode=session-link&
 * session=<id>&cohort=<cohortId>&name=<displayName>. The player joins as the
 * test player the class and the name make (testPlayerId,
 * domains/entry-links.ts, which the auth service works out from them) — its
 * id is never in the URL, so an administrator can edit the name before
 * sharing the link.
 *
 * The link is checked first (get_world_session): the session's world, and
 * its window as the server keeps it. A link opened before the window says
 * when it opens, and turns to Join by itself as it does; one opened after
 * the session says it has ended. In the window, the gold Join session joins
 * (asking for full screen with the press, as a browser wants); from the press
 * the card is the student join's (JoinStatus): the four steps, a session
 * whose match is not up yet waited for, any other refusal said by its code
 * with Retry where a second try can work.
 */

import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readLink } from '../../domains/entry-links';
import { incompleteLink, notOpenYet, readJoinRefusal, refusalWords, type JoinRefusal } from '../../domains/join-refusals';
import { checkSessionWindow, fetchWorldSession, type WorldSessionData } from '../../domains/sessions';
import { authService } from '../../services/authentication/auth-service';
import { kickOffDocumentFullscreenFromGesture, shouldEnforceMatchFullscreen } from '../../utils/fullscreen';
import { useNowSeconds } from '../../utils/use-now-seconds';
import { EntryCard, EntryFacts, EntryWho, JoinStatus, RefusalNotice } from './entry-card';
import { EntryFrame } from './entry-frame';
import { useJoinState, type JoinState } from './use-join-state';

/** The link's session, as checked: on its way, what the server holds, or what refused it. */
type LinkCheck =
  | { status: 'checking' }
  | { status: 'checked'; session: WorldSessionData }
  | { status: 'refused'; refusal: JoinRefusal };

/**
 * The session a link names, checked with the server (get_world_session): on
 * its way, the session and its window, or what refused the check — and
 * `recheck`, which asks again.
 */
function useLinkCheck(sessionId: string | undefined): { check: LinkCheck; recheck: () => void } {
  const [check, setCheck] = useState<LinkCheck>({ status: 'checking' });
  // Which check of the link this is: Retry after a failed check asks again.
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    fetchWorldSession(sessionId).then(
      (session) => live && setCheck({ status: 'checked', session }),
      async (error: unknown) => {
        const refusal = await readJoinRefusal(error);
        if (live) setCheck({ status: 'refused', refusal });
      },
    );
    return () => {
      live = false;
    };
  }, [sessionId, round]);

  const recheck = () => {
    setCheck({ status: 'checking' });
    setRound((r) => r + 1);
  };
  return { check, recheck };
}

/** Before the press: what the link's check, and then the session's window, let the card offer. */
function beforeThePress(check: LinkCheck, now: number, t: TFunction): JoinState {
  if (check.status === 'refused') return { kind: 'refused', words: refusalWords(check.refusal) };
  if (check.status === 'checking') return { kind: 'checking', says: t('sessionLink.validating') };
  const where = checkSessionWindow(check.session, now);
  if (where === 'early') return { kind: 'refused', words: notOpenYet(check.session.opensAtUtc) };
  if (where === 'ended') return { kind: 'refused', words: refusalWords({ code: 'session_ended' }) };
  return { kind: 'ready', label: t('sessionLink.joinSessionButton'), hint: t('sessionLink.readyToJoinHint') };
}

export function SessionLinkLogin({ search }: { search: string }) {
  const { t } = useTranslation();
  const link = useMemo(() => readLink(search, 'session-link'), [search]);
  const { check, recheck } = useLinkCheck(link.parts?.session);
  const session = check.status === 'checked' ? check.session : null;
  // The player has pressed Join session: from then the card is the join's.
  const [pressed, setPressed] = useState(false);
  // The window is read against the clock until the press: a link opened early turns to Join by itself.
  const now = useNowSeconds(!pressed);

  /** Sends the join: the player's own, or one sent while waiting for the session to start. */
  const join = useCallback(
    (waiting: boolean) => {
      if (link.missing || !session) return;
      const { name, session: sessionId, cohort } = link.parts;
      void authService.connectFromSessionLink(session.worldName, name, sessionId, cohort, { waiting });
    },
    [link, session],
  );
  const askAgain = useCallback(() => join(true), [join]);
  const joinState = useJoinState(askAgain);

  /** The press that joins: full screen asked for with it, as a browser wants a gesture. */
  const joinNow = () => {
    if (shouldEnforceMatchFullscreen()) kickOffDocumentFullscreenFromGesture();
    setPressed(true);
    join(false);
  };

  const frame = { search, title: t('entry.join.title'), meaning: t('entry.join.subtitle') };

  // A link cut short: only a new one mends it, and it names no one to show.
  if (link.missing) {
    return (
      <EntryFrame {...frame}>
        <EntryCard>
          <RefusalNotice words={incompleteLink(t('entry.refusal.askNewLink'))} />
        </EntryCard>
      </EntryFrame>
    );
  }

  // Before the press the link's check and the session's window say what the
  // card offers; from the press, the join. Retry after a failed check asks for
  // the session again.
  const state = pressed ? joinState : beforeThePress(check, now, t);
  const onJoin = check.status === 'refused' ? recheck : pressed ? () => join(false) : joinNow;

  return (
    <EntryFrame {...frame} kicker={session ? [session.worldName] : undefined}>
      <EntryCard>
        <EntryWho label={t('entry.player')} name={link.parts.name} />
        <EntryFacts facts={[{ label: t('entry.fact.class'), value: link.parts.cohort }]} />
        <JoinStatus state={state} onJoin={onJoin} ask="admin" />
      </EntryCard>
    </EntryFrame>
  );
}
